import { spacesSanitizer } from 'utils/sanitizers';
import CheckIcon from 'assets/icons/check-circle.svg?react';
import classes from 'classnames';
import ErrorIcon from 'assets/icons/error-circle.svg?react';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import EyeClosedIcon from 'assets/icons/eye-closed.svg?react';
import EyeOpenIcon from 'assets/icons/eye-open.svg?react';
import React, { memo, useCallback, useState } from 'react';
import styles from './Input.module.scss';

export type Props = {
  type?: 'text' | 'password' | 'tel' | 'email';
  value?: string | number;
  name?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | boolean;
  isTouched?: boolean;
  placeholder?: string;
  postFix?: string;
  showPasswordReveal?: boolean;
  dataTest?: string;
  stackable?: boolean;
  className?: string;
  classNameField?: string;
  classNameInput?: string;
  allowSpaces?: boolean;
  autoComplete?: 'on' | 'off';
};

export default memo(function Input({
  type = 'text',
  onChange,
  value = '',
  name,
  onBlur,
  error,
  isTouched,
  placeholder,
  postFix,
  showPasswordReveal = false,
  dataTest = 'input',
  stackable = false,
  className,
  classNameInput,
  classNameField,
  autoComplete,
  allowSpaces = false,
}: Props) {
  const [passwordVisibility, setPasswordVisibility] = useState<
    'password' | 'text'
  >('password');

  const handlerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!allowSpaces) {
      event.target.value = spacesSanitizer(event.target.value);
    }
    onChange?.(event, event.target.value);
  };

  const handlePasswordToggle = useCallback(() => {
    setPasswordVisibility(
      passwordVisibility === 'password' ? 'text' : 'password',
    );
  }, [passwordVisibility]);

  const showError = isTouched && error;
  const showCheck = isTouched && !error;
  const EyeIcon =
    passwordVisibility === 'password' ? EyeClosedIcon : EyeOpenIcon;

  return (
    <div className={classes(styles.container, className)}>
      <div
        className={classes(
          styles.field,
          stackable && styles.stackable,
          showError && styles['field-error'],
          classNameField,
        )}
      >
        <input
          autoComplete={autoComplete}
          data-test={dataTest}
          data-testid={dataTest}
          className={classes(
            styles.input,
            showError && styles['input-error'],
            classNameInput,
          )}
          name={name}
          id={name}
          type={type === 'password' ? passwordVisibility : type}
          onChange={handlerChange}
          value={value}
          onBlur={onBlur}
        />

        <label
          htmlFor={name}
          className={classes(
            styles.label,
            showError && styles['label-error'],
            value.toString().length && styles.float,
          )}
        >
          {placeholder}
        </label>

        <div
          className={styles.badges}
          onClick={showPasswordReveal ? handlePasswordToggle : undefined}
          data-test={`${dataTest}-password-reveal-handler`}
          data-testid={`${dataTest}-password-reveal-handler`}
        >
          {showPasswordReveal && (
            <EyeIcon
              className={classes(styles.badge, styles['password-icon'])}
              data-test={`${dataTest}-password-eye-icon`}
              data-testid={`${dataTest}-password-eye-icon`}
            />
          )}
          {postFix && (
            <span
              className={classes(
                styles.badge,
                styles['post-fix'],
                showError && styles['post-fix-error'],
              )}
            >
              {postFix}
            </span>
          )}
          {(showError || showCheck) && (
            <div
              className={classes(
                styles.divider,
                showError && styles['divider-error'],
              )}
            />
          )}
          {showError && (
            <ErrorIcon
              className={classes(styles.badge, styles['error-icon'])}
            />
          )}
          {showCheck && <CheckIcon className={styles.badge} />}
        </div>
      </div>

      {showError && typeof error !== 'boolean' && (
        <div className={styles['error-message']}>
          <ErrorMessage errors={error} dataTest={`${dataTest}-error`} />
        </div>
      )}
    </div>
  );
});
